import { useState, ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  Home,
  MessageSquare,
  Calendar,
  Users,
  Handshake,
  Wrench,
  BarChart3,
  Settings,
  Menu,
  X,
  ExternalLink,
  LogOut,
  ChevronDown
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface AdminLayoutProps {
  children: ReactNode;
}

const AdminLayout = ({ children }: AdminLayoutProps) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  const navigation = [
    {
      name: 'Dashboard Overview',
      href: '/admin/dashboard',
      icon: Home,
      description: 'Vue d\'ensemble'
    },
    {
      name: 'Messages & Demandes',
      href: '/admin/messages',
      icon: MessageSquare,
      description: 'Gestion des demandes'
    },
    {
      name: 'Suivi Réservations',
      href: '/admin/reservations',
      icon: Calendar,
      description: 'Tracking réservations'
    },
    {
      name: 'Espace Équipe',
      href: '/admin/equipe',
      icon: Users,
      description: 'Gestion équipe'
    },
    {
      name: 'Espace Partenaires',
      href: '/admin/partenaires',
      icon: Handshake,
      description: 'Gestion partenaires'
    },
    {
      name: 'Technique & Maintenance',
      href: '/admin/technique',
      icon: Wrench,
      description: 'Maintenance vélos'
    },
    {
      name: 'Statistiques',
      href: '/admin/statistiques',
      icon: BarChart3,
      description: 'Analytics & rapports'
    },
    {
      name: 'Paramètres',
      href: '/admin/parametres',
      icon: Settings,
      description: 'Configuration'
    }
  ];

  const handleLogout = () => {
    localStorage.removeItem('hirondelles_admin_portal_access');
    localStorage.removeItem('hirondelles_admin_access');
    window.location.hash = '/admin';
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-72 bg-white shadow-xl transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:flex-shrink-0 lg:h-auto ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          {/* Sidebar header */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200 bg-primary">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center mr-3">
                <span className="text-primary font-bold text-sm">LH</span>
              </div>
              <div>
                <h1 className="text-white font-bold text-lg">Admin Portal</h1>
                <p className="text-primary-foreground/80 text-xs">Les Hirondelles</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-white hover:bg-primary/20"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 group ${
                    isActive
                      ? 'bg-primary text-white shadow-md'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-primary'
                  }`}
                >
                  <item.icon className={`mr-3 h-5 w-5 ${
                    isActive ? 'text-white' : 'text-gray-500 group-hover:text-primary'
                  }`} />
                  <div>
                    <div className="font-medium">{item.name}</div>
                    <div className={`text-xs ${
                      isActive ? 'text-white/80' : 'text-gray-500'
                    }`}>
                      {item.description}
                    </div>
                  </div>
                </Link>
              );
            })}
          </nav>

          {/* Sidebar footer */}
          <div className="p-4 border-t border-gray-200">
            <div className="text-xs text-gray-500 mb-3">
              SARL LAUDARE - Les Hirondelles
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="w-full text-gray-600 hover:text-red-600 hover:border-red-200"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Déconnexion
            </Button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-0 lg:pl-0">
        {/* Top header */}
        <header className="bg-white shadow-sm border-b border-gray-200 h-16 flex items-center justify-between px-6 flex-shrink-0">
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden mr-4"
            >
              <Menu className="h-5 w-5" />
            </Button>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {navigation.find(item => item.href === location.pathname)?.name || 'Admin Portal'}
              </h2>
              <p className="text-sm text-gray-500">
                {navigation.find(item => item.href === location.pathname)?.description || 'Back-office Les Hirondelles'}
              </p>
            </div>
          </div>

          {/* Quick access dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                Accès rapide
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem asChild>
                <a href="/#/" target="_blank" className="flex items-center">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Site public
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <a href="/#/reserver" target="_blank" className="flex items-center">
                  <Calendar className="h-4 w-4 mr-2" />
                  Réservations
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <a href="/#/partenaires" target="_blank" className="flex items-center">
                  <Handshake className="h-4 w-4 mr-2" />
                  Portail partenaires
                </a>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                <LogOut className="h-4 w-4 mr-2" />
                Déconnexion
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          <div className="max-w-7xl mx-auto p-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;